const endpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT as string | undefined

declare global { interface Navigator { globalPrivacyControl?: boolean } }

export type AnalyticsEvent = 'page_view' | 'app_store_click' | 'sibling_game_click' | 'gallery_view' | 'match_demo' | 'guide_complete' | 'preview_play' | 'purchase_learn_more' | 'act_preview_select' | 'act_preview_play' | 'act_scrub' | 'lightbox_open' | 'web_preview_complete' | 'experiment_exposure' | 'section_nav' | 'faq_open' | 'performance_metric'

export function experimentVariant(name: string): 'control' | 'story-first' {
  const key = `matchflea-experiment:${name}`
  try {
    const existing = sessionStorage.getItem(key)
    if (existing === 'control' || existing === 'story-first') return existing
    const variant = crypto.getRandomValues(new Uint8Array(1))[0] % 2 === 0 ? 'control' : 'story-first'
    sessionStorage.setItem(key, variant)
    return variant
  } catch { return 'control' }
}

export function track(event: AnalyticsEvent, locale: string, page = 'home', detail?: string) {
  if (!endpoint || navigator.doNotTrack === '1' || navigator.globalPrivacyControl === true) return
  const experiments: Record<string, string> = {}
  try {
    const prefix = 'matchflea-experiment:'
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index)
      if (key?.startsWith(prefix)) experiments[key.slice(prefix.length)] = sessionStorage.getItem(key) ?? ''
    }
  } catch { /* Measurement remains anonymous when storage is unavailable. */ }
  const payload = JSON.stringify({ event, locale, page, detail, experiments, path: window.location.pathname, timestamp: new Date().toISOString() })
  if (navigator.sendBeacon) navigator.sendBeacon(endpoint, new Blob([payload], { type: 'application/json' }))
  else void fetch(endpoint, { method: 'POST', body: payload, headers: { 'content-type': 'application/json' }, keepalive: true, credentials: 'omit' })
}

export function installPerformanceMonitoring(locale: string) {
  if (!endpoint || !('PerformanceObserver' in window)) return () => undefined
  const observers: PerformanceObserver[] = []
  const report = (name: string, value: number) => track('performance_metric', locale, 'home', `${name}:${Math.round(value)}`)
  try {
    const paint = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) if (entry.name === 'first-contentful-paint') report('fcp', entry.startTime)
    })
    paint.observe({ type: 'paint', buffered: true }); observers.push(paint)
    const largest = new PerformanceObserver((list) => {
      const entry = list.getEntries().at(-1)
      if (entry) report('lcp', entry.startTime)
    })
    largest.observe({ type: 'largest-contentful-paint', buffered: true }); observers.push(largest)
    let layoutShift = 0
    const layout = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as (PerformanceEntry & { value?: number; hadRecentInput?: boolean })[]) {
        if (!entry.hadRecentInput) layoutShift += entry.value ?? 0
      }
    })
    layout.observe({ type: 'layout-shift', buffered: true }); observers.push(layout)
    const flush = () => { if (document.visibilityState === 'hidden' && layoutShift) report('cls', layoutShift * 1000) }
    document.addEventListener('visibilitychange', flush)
    return () => { observers.forEach((observer) => observer.disconnect()); document.removeEventListener('visibilitychange', flush) }
  } catch { return () => observers.forEach((observer) => observer.disconnect()) }
}
